import { useState } from "react";
import { Camera } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "./ui/sonner";

/** Mandatory Stranger identity photo — required at signup, admin-reviewed, hidden from other
 * Strangers. Reused on the registration wizard and the dashboard (to re-upload after a rejection). */
export const ProfilePhotoUpload = ({ user, onUploaded }) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/uploads/profile-photo", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded?.(data);
      toast.success("Photo uploaded — pending admin review.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const hasPhoto = !!user?.profile_photo_url;
  const photoSrc = preview || (hasPhoto && user?.id ? `${process.env.REACT_APP_BACKEND_URL}/api/uploads/profile-photo/${user.id}?v=${user.photo_submitted_at || ""}` : null);

  return (
    <div data-testid="profile-photo-upload">
      <div className="w-32 h-32 rounded-full border-2 border-soft-gray overflow-hidden bg-warm-white flex items-center justify-center mx-auto mb-4">
        {photoSrc ? (
          <img src={photoSrc} alt="Your profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
        ) : (
          <Camera className="w-8 h-8 text-midnight-black/30" />
        )}
      </div>
      {user?.photo_status === "rejected" && (
        <p className="text-sm font-bold text-red-600 text-center mb-3 max-w-sm mx-auto" data-testid="photo-rejected-message">
          Your previous photo wasn't approved{user.photo_rejection_reason ? `: ${user.photo_rejection_reason}` : "."} Please upload a new one.
        </p>
      )}
      {user?.photo_status === "pending" && (
        <p className="text-xs font-bold text-stranger-orange text-center mb-3 uppercase tracking-wide" data-testid="photo-pending-message">
          Pending admin review
        </p>
      )}
      {user?.photo_status === "approved" && (
        <p className="text-xs font-bold text-midnight-black/60 text-center mb-3 uppercase tracking-wide" data-testid="photo-approved-message">
          Approved
        </p>
      )}
      <label className="block text-center">
        <span className="inline-block cursor-pointer text-xs font-bold tracking-widest uppercase border-2 border-midnight-black px-6 py-3 rounded-full hover:bg-midnight-black hover:text-clean-white transition-colors">
          {uploading ? "Uploading..." : hasPhoto ? "Replace Photo" : "Upload Photo"}
        </span>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          data-testid="profile-photo-input"
          onChange={handleFile}
          disabled={uploading}
        />
      </label>
    </div>
  );
};
